import React from 'react';
import ModelsAPI from '../../api/models';
import { AIModel } from '../../api/structs/model';
import { ReviewCapabilityFields } from '../../api/structs/pipeline';
import ProviderModelPicker, {
  PickerModelOption,
  firstModelFor,
  modelOptionsFor,
  providerOptionsFor,
} from '../ui/ProviderModelPicker';
import ReviewCapabilitiesForm from '../pipeline/reviewer/ReviewCapabilitiesForm';
import ReviewPermissionsFields, {
  clampCrossSceneMax,
} from '../pipeline/reviewer/ReviewPermissionsFields';
import ReviewerGraph from '../pipeline/reviewer/ReviewerGraph';
import applyReviewOverrides, {
  baselineReviewCheckpoint,
} from '../pipeline/reviewer/applyReviewOverrides';
import {
  HARD_REVIEW_CAPABILITIES,
  resolveReviewCapabilities,
} from '../pipeline/reviewer/resolveReviewCapabilities';
import {
  CLIP_QA_MODES,
  CLIP_QA_VIDEO_INPUT_MODES,
  DEFAULT_REVIEWER_SETTINGS,
  REVIEW_MODES,
  REVIEW_SEVERITY_GATES,
  ClipQAMode,
  ClipQAVideoInput,
  ReviewMode,
  ReviewSeverityGate,
  ReviewerSettings,
  SettingsAPI,
  parseReviewerSettings,
} from '../../api/settings';
import { useToast } from '../../hooks/useToast';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));


interface SwitchFieldProps {
  label: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
  disabledTitle?: string;
}

/** A boolean toggle in the app's existing "theme-switch" visual language. */
export const SwitchField: React.FC<SwitchFieldProps> = ({ label, checked, onToggle, disabled, disabledTitle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    title={disabled ? disabledTitle : undefined}
    className="theme-switch w-full justify-between disabled:opacity-50 disabled:cursor-not-allowed"
    onClick={onToggle}
  >
    <span className="theme-switch-label">{label}</span>
    <span className="theme-switch-track">
      <span className={`theme-switch-thumb ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </span>
  </button>
);

const PERMISSIONS_HELP_TEXT = 'These bound what the reviewer may do. Off = judge and report only.';

/**
 * User-scoped defaults for the AI reviewer: which models judge, how strict the
 * gates are, and how many repair attempts a run may spend. A checkpoint's own
 * `review` block overrides any of this; templates that declare nothing inherit
 * it wholesale.
 */
const ReviewerSettingsSection: React.FC = () => {
  const toast = useToast();
  const [values, setValues] = React.useState<ReviewerSettings>({ ...DEFAULT_REVIEWER_SETTINGS });
  const [models, setModels] = React.useState<AIModel[]>([]);
  const [modelsError, setModelsError] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    void SettingsAPI.get()
      .then((settings) => {
        if (cancelled) return;
        setValues(parseReviewerSettings(settings));
        setLoadError(false);
      })
      // Unsaved settings are the normal case, but a failed GET is not: say so
      // rather than presenting the hard defaults as if they were the user's.
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    void ModelsAPI.getImageInputModels()
      .then((list) => {
        if (cancelled) return;
        setModels(list);
        setModelsError(false);
      })
      .catch(() => {
        if (!cancelled) setModelsError(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const checkProviderOptions = providerOptionsFor(models, values.check_provider);
  const checkModelOptions = modelOptionsFor(models, values.check_provider, values.check_model);
  const confirmProviderOptions = providerOptionsFor(models, values.confirm_provider);
  const confirmModelOptions = modelOptionsFor(models, values.confirm_provider, values.confirm_model);

  // Absent `clip_qa.model` means "inherit the confirm tier", not a delisted
  // model — default the provider control to confirm's so the row has
  // something sensible to show before the user picks an explicit override.
  const clipQaProvider = values.clip_qa.model?.provider || values.confirm_provider;
  const clipQaModel = values.clip_qa.model?.model || '';
  const clipQaProviderOptions = providerOptionsFor(models, clipQaProvider);
  const clipQaModelOptions: PickerModelOption[] = [
    { value: '', label: 'Inherit confirm model' },
    ...modelOptionsFor(models, clipQaProvider, clipQaModel),
  ];

  // Switching provider lands on that provider's first catalog model (or
  // clears the field when it has none) — provider and model always move
  // together, so the engine never sees a mismatched pair.
  const pickCheckProvider = (provider: string) =>
    setValues((current) => ({ ...current, check_provider: provider, check_model: firstModelFor(models, provider) }));
  const pickCheckModel = (model: string) => setValues((current) => ({ ...current, check_model: model }));

  const pickConfirmProvider = (provider: string) =>
    setValues((current) => ({
      ...current,
      confirm_provider: provider,
      confirm_model: firstModelFor(models, provider),
    }));
  const pickConfirmModel = (model: string) => setValues((current) => ({ ...current, confirm_model: model }));

  const pickClipQaProvider = (provider: string) => {
    const nextModel = firstModelFor(models, provider);
    setValues((current) => {
      if (!nextModel) {
        const clipQa = { ...current.clip_qa };
        delete clipQa.model;
        return { ...current, clip_qa: clipQa };
      }
      return { ...current, clip_qa: { ...current.clip_qa, model: { provider, model: nextModel } } };
    });
  };

  // Empty value ⇒ "Inherit confirm model": drop the override entirely rather
  // than storing a half-formed ref.
  const pickClipQaModel = (model: string) => {
    if (!model) {
      setValues((current) => {
        const clipQa = { ...current.clip_qa };
        delete clipQa.model;
        return { ...current, clip_qa: clipQa };
      });
      return;
    }
    setValues((current) => ({
      ...current,
      clip_qa: { ...current.clip_qa, model: { provider: clipQaProvider, model } },
    }));
  };

  const togglePermission = (
    key:
      | 'pipeline_regenerate'
      | 'reconcile'
      | 'attachment_rewire'
      | 'drop_closing_frame'
      | 'adjust_video_prompt'
      | 'adjust_prompts_before_generation'
  ) =>
    setValues((current) => ({
      ...current,
      permissions: { ...current.permissions, [key]: !current.permissions[key] },
    }));

  // The ten capability fields. An EMPTY block is stored as absent: "never
  // chose" and "deliberately all off" are different statements to the engine,
  // and only the absent one inherits.
  const setCapabilities = (next: ReviewCapabilityFields) =>
    setValues((current) => {
      if (Object.keys(next).length === 0) {
        const withoutCapabilities = { ...current };
        delete withoutCapabilities.capabilities;
        return withoutCapabilities;
      }
      return { ...current, capabilities: next };
    });

  // What the account alone would give a typical two-frame scene. There is no
  // template here to read a profile for, so the picture is drawn from the
  // synthetic baseline through the very same overlay a real checkpoint uses.
  const accountBaseline = applyReviewOverrides(
    baselineReviewCheckpoint({
      check: { provider: values.check_provider, model: values.check_model },
      confirm: { provider: values.confirm_provider, model: values.confirm_model },
      confidence_gate: values.confidence_gate,
      severity_gate: values.severity_gate,
      max_attempts: values.max_attempts,
    }),
    resolveReviewCapabilities(values.capabilities)
  );

  const toggleClipQa = (key: 'enabled' | 'allow_trim' | 'allow_rerender') =>
    setValues((current) => ({
      ...current,
      clip_qa: { ...current.clip_qa, [key]: !current.clip_qa[key] },
    }));

  const handleSave = async () => {
    setSaving(true);
    const maxAttempts = clamp(Math.round(values.max_attempts), 0, 3);
    const payload: ReviewerSettings = {
      ...values,
      confidence_gate: clamp(values.confidence_gate, 0, 1),
      max_attempts: maxAttempts,
      // pipeline_max_attempts is an alias of max_attempts — the engine keeps
      // both in sync, so the panel must send them equal rather than trusting
      // two independent fields to agree.
      permissions: {
        ...values.permissions,
        pipeline_max_attempts: maxAttempts,
        // The field clamps as it is typed; a section loaded from storage may
        // still carry an out-of-range number, so the save states the rule too.
        cross_scene_max_per_run: clampCrossSceneMax(values.permissions.cross_scene_max_per_run),
      },
      clip_qa: {
        ...values.clip_qa,
        max_rerenders: clamp(Math.round(values.clip_qa.max_rerenders), 0, 2),
        confidence_gate: clamp(values.clip_qa.confidence_gate, 0, 1),
      },
    };
    try {
      const saved = await SettingsAPI.putReviewer(payload);
      // Normalize through the same parser as a load, so a lean response body
      // can never leave the panel holding a half-populated section.
      setValues(parseReviewerSettings({ reviewer: saved }));
      toast({ text: 'Reviewer settings saved', level: 'success' });
    } catch {
      toast({ text: 'Failed to save reviewer settings', level: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const labelClass = 'block text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-1';

  return (
    <>
      <div className="mb-4 flex-shrink-0">
        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">
          Reviewer
        </h3>
        <p className="text-xs text-[var(--app-text-muted)]">
          Two-tier AI review of generated steps: a cheap model judges everything, a strong model
          adjudicates what the cheap one failed. Templates inherit these unless a checkpoint's
          review block overrides them.
        </p>
      </div>

      {loading ? (
        <p className="text-xs text-[var(--app-text-muted)]">Loading…</p>
      ) : (
        <div className="flex flex-col gap-4 max-w-md" data-testid="reviewer-settings">
          {loadError && (
            <p className="text-xs text-amber-400">
              Couldn't load your saved reviewer settings — showing the built-in defaults. Saving
              will overwrite whatever is stored.
            </p>
          )}

          <div>
            <h4 className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-2">
              Check model (cheap, judges everything)
            </h4>
            <ProviderModelPicker
              providerOptions={checkProviderOptions}
              provider={values.check_provider}
              onProviderChange={pickCheckProvider}
              providerAriaLabel="Check model provider"
              modelOptions={checkModelOptions}
              model={values.check_model}
              onModelChange={pickCheckModel}
              modelAriaLabel="Check model"
            />
          </div>

          <div>
            <h4 className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-2">
              Confirm model (strong, adjudicates fails)
            </h4>
            <ProviderModelPicker
              providerOptions={confirmProviderOptions}
              provider={values.confirm_provider}
              onProviderChange={pickConfirmProvider}
              providerAriaLabel="Confirm model provider"
              modelOptions={confirmModelOptions}
              model={values.confirm_model}
              onModelChange={pickConfirmModel}
              modelAriaLabel="Confirm model"
            />
          </div>

          <p className="text-[11px] text-[var(--app-text-muted)]">
            Vertex AI models are billed through your Vertex project; OpenRouter through your
            OpenRouter key.
          </p>

          {modelsError && (
            <p className="text-xs text-[var(--app-text-muted)]">
              Couldn't load the live model list — showing your saved choice. Try reopening
              Settings.
            </p>
          )}

          <div>
            <label className={labelClass} htmlFor="reviewer-default-mode">
              Default review mode
            </label>
            <select
              id="reviewer-default-mode"
              className="select w-full"
              value={values.default_review_mode}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  default_review_mode: event.target.value as ReviewMode,
                }))
              }
            >
              {REVIEW_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-[var(--app-text-muted)]">
              off: judge and badge only. on: repair confirmed blocking fails. strict: any confirmed
              fail blocks the run.
            </p>
          </div>

          <div className="flex flex-wrap gap-4">
            <div className="w-32">
              <label className={labelClass} htmlFor="reviewer-confidence-gate">
                Confidence gate
              </label>
              <input
                id="reviewer-confidence-gate"
                type="number"
                min={0}
                max={1}
                step={0.05}
                inputMode="decimal"
                className="input w-full"
                value={values.confidence_gate}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    confidence_gate: Number(event.target.value),
                  }))
                }
              />
            </div>

            <div className="w-40">
              <label className={labelClass} htmlFor="reviewer-severity-gate">
                Severity gate
              </label>
              <select
                id="reviewer-severity-gate"
                className="select w-full"
                value={values.severity_gate}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    severity_gate: event.target.value as ReviewSeverityGate,
                  }))
                }
              >
                {REVIEW_SEVERITY_GATES.map((gate) => (
                  <option key={gate} value={gate}>
                    {gate}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p className="text-[11px] text-[var(--app-text-muted)]">
            Only vision-capable chat models are listed — a judge has to see the still it grades.
            Every judge call is an external, credit-billed request.
          </p>

          <div className="border-t border-[var(--border-subtle)] pt-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">
              Capabilities
            </h4>
            <p className="text-[11px] text-[var(--app-text-muted)] mb-3">
              What the judge is shown and what it rules on, for every pipeline that does not say
              otherwise.
            </p>

            <div className="mb-3" data-testid="reviewer-account-baseline">
              <p className="text-[11px] text-[var(--app-text-muted)] mb-1">
                What this would give a typical two-frame scene:
              </p>
              <ReviewerGraph
                compact
                checkpoint={accountBaseline}
                mode={values.default_review_mode}
                templateId=""
                checkpointId={accountBaseline.checkpoint_id}
              />
            </div>

            <ReviewCapabilitiesForm
              layer="account"
              value={values.capabilities || {}}
              inherited={HARD_REVIEW_CAPABILITIES}
              showPairJudge
              models={models}
              // The account IS the top of the cascade: the pair judge's caption
              // can only promise the tiers this very pane is editing.
              inheritedTiers={{
                check: { provider: values.check_provider, model: values.check_model },
                confirm: { provider: values.confirm_provider, model: values.confirm_model },
                pair: {},
              }}
              onChange={setCapabilities}
            />
          </div>

          <div className="border-t border-[var(--border-subtle)] pt-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">
              Permissions
            </h4>
            <p className="text-[11px] text-[var(--app-text-muted)] mb-3">{PERMISSIONS_HELP_TEXT}</p>
            <div className="flex flex-col gap-2">
              <SwitchField
                label="Regenerate in pipelines"
                checked={values.permissions.pipeline_regenerate}
                onToggle={() => togglePermission('pipeline_regenerate')}
              />
              <SwitchField
                label="Reconcile plan"
                checked={values.permissions.reconcile}
                onToggle={() => togglePermission('reconcile')}
              />
              <SwitchField
                label="Rewire attachments"
                checked={values.permissions.attachment_rewire}
                onToggle={() => togglePermission('attachment_rewire')}
              />
              <SwitchField
                label="May drop closing frames"
                checked={values.permissions.drop_closing_frame}
                onToggle={() => togglePermission('drop_closing_frame')}
              />
              {values.permissions.drop_closing_frame && (
                <p className="text-[11px] text-[var(--app-text-muted)]">
                  While reviewing a scene&apos;s closing frame the reviewer may switch that frame
                  OFF instead of regenerating it, when it judges the clip converges better as a
                  one-frame scene. The frame stays browsable on the step and one click re-enables
                  it.
                </p>
              )}
              <SwitchField
                label="Reviewer may adjust the prompts before generating"
                checked={values.permissions.adjust_prompts_before_generation}
                onToggle={() => togglePermission('adjust_prompts_before_generation')}
              />
              {values.permissions.adjust_prompts_before_generation && (
                <p className="text-[11px] text-[var(--app-text-muted)]">
                  Before a scene&apos;s first image is generated, the reviewer reads its opening,
                  closing and motion prompts against the scene&apos;s own contract and rewrites them
                  inside the approved story — one cheap text call per scene, never repeated on a
                  resume. It never blocks a scene: if the check fails, the original prompts are used.
                </p>
              )}
              <SwitchField
                label="Fix the motion prompt"
                checked={values.permissions.adjust_video_prompt}
                onToggle={() => togglePermission('adjust_video_prompt')}
              />
              {values.permissions.adjust_video_prompt && (
                <p className="text-[11px] text-[var(--app-text-muted)]">
                  When both frames of a scene are right but the movement between them was
                  mis-described, the reviewer may rewrite the scene&apos;s video prompt. A second
                  judge — the keeps check — first confirms the rewrite keeps the same characters,
                  beats, dialogue and duration. A frame note that the motion may not start cleanly
                  from its frame is fixed the same way, never by re-rolling the frame.
                </p>
              )}

              <div className="w-48 mt-1">
                <label className={labelClass} htmlFor="reviewer-pipeline-max-attempts">
                  Max regenerations per checkpoint
                </label>
                <input
                  id="reviewer-pipeline-max-attempts"
                  type="number"
                  min={0}
                  max={3}
                  step={1}
                  inputMode="numeric"
                  className="input w-full"
                  value={values.max_attempts}
                  onChange={(event) =>
                    setValues((current) => ({ ...current, max_attempts: Number(event.target.value) }))
                  }
                />
              </div>
              <p className="text-[11px] text-[var(--app-text-muted)]">
                0 = never regenerate; with &apos;Regenerate in pipelines&apos; on, a confirmed fail then pauses the
                checkpoint for you.
              </p>

              {/* The same two controls a pipeline's AI Reviewer tab shows —
                  one form, so the account baseline and the override cannot
                  drift apart. The account layer always states a value, so it
                  is rendered without the "inherits: …" captions. */}
              <ReviewPermissionsFields
                idPrefix="reviewer-permissions"
                value={{
                  cross_scene_regenerate: values.permissions.cross_scene_regenerate,
                  cross_scene_max_per_run: values.permissions.cross_scene_max_per_run,
                }}
                inherited={{
                  cross_scene_regenerate:
                    DEFAULT_REVIEWER_SETTINGS.permissions.cross_scene_regenerate,
                  cross_scene_max_per_run:
                    DEFAULT_REVIEWER_SETTINGS.permissions.cross_scene_max_per_run,
                }}
                onChange={(next) =>
                  setValues((current) => ({
                    ...current,
                    permissions: {
                      ...current.permissions,
                      cross_scene_regenerate:
                        next.cross_scene_regenerate ?? current.permissions.cross_scene_regenerate,
                      cross_scene_max_per_run:
                        next.cross_scene_max_per_run ?? current.permissions.cross_scene_max_per_run,
                    },
                  }))
                }
              />
            </div>
          </div>

          <div className="border-t border-[var(--border-subtle)] pt-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">
              Clip QA (Phase 2)
            </h4>
            <p className="text-[11px] text-[var(--app-text-muted)] mb-3">{PERMISSIONS_HELP_TEXT}</p>
            <div className="flex flex-col gap-3">
              <SwitchField
                label="Enable Clip QA"
                checked={values.clip_qa.enabled}
                onToggle={() => toggleClipQa('enabled')}
              />

              <div>
                <label className={labelClass} htmlFor="reviewer-clip-qa-mode">
                  Mode
                </label>
                <select
                  id="reviewer-clip-qa-mode"
                  className="select w-full"
                  value={values.clip_qa.mode}
                  onChange={(event) =>
                    setValues((current) => ({
                      ...current,
                      clip_qa: { ...current.clip_qa, mode: event.target.value as ClipQAMode },
                    }))
                  }
                >
                  {CLIP_QA_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="w-40">
                  <label className={labelClass} htmlFor="reviewer-clip-qa-max-rerenders">
                    Max re-renders per clip
                  </label>
                  <input
                    id="reviewer-clip-qa-max-rerenders"
                    type="number"
                    min={0}
                    max={2}
                    step={1}
                    inputMode="numeric"
                    className="input w-full"
                    value={values.clip_qa.max_rerenders}
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        clip_qa: { ...current.clip_qa, max_rerenders: Number(event.target.value) },
                      }))
                    }
                  />
                </div>

                <div className="w-40">
                  <label className={labelClass} htmlFor="reviewer-clip-qa-confidence-gate">
                    Clip QA confidence gate
                  </label>
                  <input
                    id="reviewer-clip-qa-confidence-gate"
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    inputMode="decimal"
                    className="input w-full"
                    value={values.clip_qa.confidence_gate}
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        clip_qa: { ...current.clip_qa, confidence_gate: Number(event.target.value) },
                      }))
                    }
                  />
                  <p className="mt-1 text-[11px] text-[var(--app-text-muted)]">0 inherits the pipeline gate.</p>
                </div>
              </div>

              <SwitchField
                label="Write trim points"
                checked={values.clip_qa.allow_trim}
                onToggle={() => toggleClipQa('allow_trim')}
              />
              <SwitchField
                label="Allow re-render"
                checked={values.clip_qa.allow_rerender}
                onToggle={() => toggleClipQa('allow_rerender')}
              />

              <div>
                <h4 className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-2">
                  Clip QA model
                </h4>
                <ProviderModelPicker
                  providerOptions={clipQaProviderOptions}
                  provider={clipQaProvider}
                  onProviderChange={pickClipQaProvider}
                  providerAriaLabel="Clip QA model provider"
                  modelOptions={clipQaModelOptions}
                  model={clipQaModel}
                  onModelChange={pickClipQaModel}
                  modelAriaLabel="Clip QA model"
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="reviewer-clip-qa-video-input">
                  Clip video input
                </label>
                <select
                  id="reviewer-clip-qa-video-input"
                  className="select w-full"
                  value={values.clip_qa.video_input}
                  onChange={(event) =>
                    setValues((current) => ({
                      ...current,
                      clip_qa: {
                        ...current.clip_qa,
                        video_input: event.target.value as ClipQAVideoInput,
                      },
                    }))
                  }
                >
                  {CLIP_QA_VIDEO_INPUT_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-[var(--app-text-muted)]">
                  auto: send the clip itself to video-capable judges (includes audio). frames: always
                  sampled frames.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={() => void handleSave()} disabled={saving} className="btn btn-primary btn-sm">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ReviewerSettingsSection;
